'use client'

import React, { useState } from 'react'
import { Section } from '@/components/Section'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/input'
import { jsonp, toQueryString } from './utils'
import { Label } from '@/components/ui/label'
import { MailchimpFormBlock } from '@/payload-types'

interface MailchimpSubscribeProps {
  prompt: string
  url: string
  render?: (props: {
    subscribe: (data: { EMAIL: string }) => void
    status: 'idle' | 'sending' | 'success' | 'error' | null
    message: string | null
  }) => React.ReactNode
}

type MailchimpFormData = {
  EMAIL: string
}

type MailchimpResponse = {
  result: string
  msg: string
}

const getAjaxUrl = (url: string): string => url.replace('/post?', '/post-json?')

function MailchimpSubscribe({ url, prompt }: MailchimpSubscribeProps) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error' | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const subscribe = (data: MailchimpFormData) => {
    const params = toQueryString(data)
    const ajaxUrl = getAjaxUrl(url) + '&' + params

    setStatus('sending')
    setMessage(null)

    jsonp(ajaxUrl, { param: 'c' }, (err: Error | null, data?: MailchimpResponse) => {
      if (err) {
        setStatus('error')
        setMessage(err.message || 'An error occurred')
      } else if (data?.result !== 'success') {
        setStatus('error')
        setMessage(data?.msg || 'Subscription failed')
      } else {
        setStatus('success')
        setMessage(data?.msg || 'Successfully subscribed!')
      }
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (email && email.includes('@')) {
      subscribe({ EMAIL: email })
    }
  }

  return (
    <div className="space-y-[20px]">
      <div className="h-[50px] flex items-end">
        {status === null && <Label className="text-dark text-body-lg">{prompt}</Label>}

        {status === 'sending' && <Label className="text-dark/50 text-lg">Sending...</Label>}

        {status === 'error' && message && (
          <Label className="text-punch text-lg" dangerouslySetInnerHTML={{ __html: message }} />
        )}

        {status === 'success' && message && (
          <Label className="text-dark text-lg" dangerouslySetInnerHTML={{ __html: message }} />
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex">
        <Input
          type="email"
          placeholder="Your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === 'sending'}
          className="flex-1 rounded-none h-[48px] focus:z-10 border-r-0 border-olive-2/50"
          required
        />
        <Button
          type="submit"
          variant="slime"
          disabled={status === 'sending'}
          className="rounded-l-none disabled:opacity-100"
        >
          {status === 'sending' ? 'Sending...' : 'Subscribe'}
        </Button>
      </form>
    </div>
  )
}

export function MailchimpFormClient({ url, prompt }: MailchimpFormBlock) {
  return (
    <Section bgClassName="bg-paper-2">
      <div className="min-h-[400px] flex flex-col pt-[50px] max-w-readable">
        <MailchimpSubscribe prompt={prompt} url={url} />
      </div>
    </Section>
  )
}
